/**
 * A query-report-header component defines the saving
 * and loading mechanisms for interacting with reports
 */
import {Component} from 'panel';

import {pluralize} from 'common/util';
import {serializeAttributes} from '../../util';
import {emitTrackEvent} from '../../util/dom';
import {registerMPElement} from '../../util/register-element';

import {TrackingEvent} from './tracking';

import template from './index.jade';

import css from './index.styl?inline';
import {SectionItem} from 'common/components/items-menu/types';

const COMPONENT_NAME = `mp-query-report-header`;

enum SaveModes {
  SaveNew = `save-new`,
  Edit = `edit`,
  Save = `save`,
}

enum HeaderAction {
  Edit = `edit`,
  Save = `save`,
  SaveNew = `save-new`,
  Load = `load`,
  Reset = `new`,
  Tour = `tour`,
}

interface QueryReportHeaderState {
  reportName: string;
  reportDesc: string;
  reportDescTruncated: boolean;
  reportHidden: boolean;
  reportLocked: boolean;
  bookmarkDrawerOpen: boolean;
  saveMode: SaveModes;
  saveModalOpen: boolean;
  editWarningModalOpen: boolean;
  bookmarks: any[];
}

const RESET_ACTION = Object.freeze({
  icon: `undo`,
  value: HeaderAction.Reset,
  label: `Reset query`,
});

const TOUR_ACTION = Object.freeze({
  icon: `feature-tour`,
  value: HeaderAction.Tour,
  label: `Launch tour`,
});

const EDIT_ACTION = Object.freeze({
  icon: `edit`,
  value: HeaderAction.Edit,
  label: `Edit report`,
});

export const QueryReportHeader = registerMPElement(
  COMPONENT_NAME,
  class extends Component<QueryReportHeaderState> {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'can-add-bookmark': `boolean`,
        'can-load': {
          type: `boolean`,
          default: true,
        },
        'can-write': `boolean`,
        'can-write-hidden': `boolean`,
        'can-write-locked': `boolean`,
        'can-save': `boolean`,
        'can-save-hidden': `boolean`,
        'can-save-locked': `boolean`,
        'computed-at': `string`,
        'control-items': {
          type: `json`,
          default: [],
          description: `Additional set of items to be rendered in the control bar, interface ControlItem[]`,
        },
        'default-reports-enabled': `boolean`,
        'empty-bookmark-name': `string`,
        'has-unsaved-changes': `boolean`,
        'num-custom-alerts': `number`,
        'num-dashboards': `number`,
        'overflow-control-items': {
          type: `json`,
          default: [],
          description: `Items to be rendered in the overflow section of the control bar, interface ControlItem[]`,
        },
        'project-id': `number`,
        'sampling-factor': `number`,
        'save-disabled-tooltip': {
          type: `string`,
          default: `Report editing is locked.`,
          description: `Message displayed in tooltip over save controls when they are disabled`,
        },
        'selected-bookmark-id': `number`,
        'selected-bookmark-user-id': `number`,
        'selected-bookmark-name': `string`,
        'selected-bookmark-description': `string`,
        'selected-bookmark-hidden': `boolean`,
        'selected-bookmark-locked': `boolean`,
        'track-name': {
          type: `string`,
          description: `If specified, dispatches '[mp-query-report-header] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
        },
        'tour-id': {
          type: `string`,
          description: `onboarding tour id`,
        },
        'tour-link': {
          type: `string`,
          description: `link to open as a fallback if the tour id is not defined`,
        },
        'user-id': `number`,
        'workspace-id': `number`,
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,

        defaultState: {
          bookmarkDrawerOpen: false,
          reportName: ``,
          reportDesc: ``,
          reportDescTruncated: true,
          reportHidden: false,
          reportLocked: false,
          saveMode: null,
          saveModalOpen: false,
          editWarningModalOpen: false,
          bookmarks: null,
        },

        helpers: {
          serializeAttributes,

          hasSlot: (name) => this.hasSlot(name),
          closeBookmarkDrawer: () => this.changeBookmarkDrawer(false),
          getHeaderActionOverflowItems: () => this.getHeaderActionOverflowItems(),
          getHeaderActionItems: () => this.getHeaderActionItems(),
          getHeaderActionItemsSlots: () => this.getHeaderActionItemsSlots(),
          hasSelectedBookmark: () => this.attr(`selected-bookmark-id`),

          bookmarkDeleted: (ev) => {
            this.dispatchEvent(new CustomEvent(`bookmarkdelete`, {detail: ev.detail}));
            this.track(TrackingEvent.ReportListDeleted);
          },

          bookmarkSelected: (ev) => {
            const {report} = ev.detail;
            const isOwner = this.attr(`user-id`) === report.userID;

            this.dispatchEvent(new CustomEvent(`bookmarkselect`, {detail: {report}}));
            this.track(TrackingEvent.ReportListSelected, {
              'is owner': isOwner,
            });
          },

          openSaveModal: () => this.openSaveModal(),
          closeSaveModal: () => this.closeSaveModal(),
          submitSaveModal: (ev) => this.submitSave(ev.detail.value),

          /** Show save modal when editing is enabled */
          editReportClicked: () => {
            const isBookmarkSelected = !!this.attr(`selected-bookmark-id`);
            this.openSaveModal(isBookmarkSelected ? SaveModes.Edit : SaveModes.Save);
          },

          getSamplingNotice: () => {
            const samplingFactor = this.attr(`sampling-factor`);
            if (samplingFactor && samplingFactor < 1) {
              return `sampled at ${samplingFactor * 100}%`;
            }

            return null;
          },

          getEditWarning: () => {
            const numDashboards = this.attr(`num-dashboards`);
            const numDashboardsText = numDashboards
              ? `${numDashboards === 1 ? `a` : numDashboards} ${pluralize(`Dashboard`, numDashboards)}`
              : ``;
            const numAlerts = this.attr(`num-custom-alerts`);
            const numAlertsText = numAlerts
              ? `${numAlerts === 1 ? `an` : numAlerts} ${pluralize(`Alert`, numAlerts)}`
              : ``;
            const conjunction = numAlerts && numDashboards ? ` and ` : ``;

            return [
              `This report is currently active on`,
              ` ${numAlertsText}${conjunction}${numDashboardsText}.`,
              ` By continuing, you’ll be updating the data across all instances.`,
              ` Would you like to save this report?`,
            ].join(``);
          },

          isSavingNewReport: () => {
            return this.state.saveMode === SaveModes.SaveNew || !this.helpers.hasSelectedBookmark();
          },

          onHeaderActionClick: (ev) => {
            switch (ev.detail.value) {
              case HeaderAction.Tour:
                this.launchTour();
                break;
              case HeaderAction.Edit:
                this.openSaveModal(SaveModes.Edit);
                break;
              case HeaderAction.Load:
                this.changeBookmarkDrawer(true);
                break;
              case HeaderAction.SaveNew:
                this.openSaveModal(SaveModes.SaveNew);
                break;
              case HeaderAction.Save:
                if (!this.helpers.hasSelectedBookmark()) {
                  // if no bookmark is currently selected, show bookmark edit modal
                  this.openSaveModal(SaveModes.Save);
                } else if (this.attr(`num-dashboards`) || this.attr(`num-custom-alerts`)) {
                  this.update({editWarningModalOpen: true});
                } else {
                  // if current bookmark is not part of dashboards or alerts, save directly
                  this.submitSave();
                }
                break;
              case HeaderAction.Reset:
                this.resetReport();
                break;
              default:
                this.dispatchEvent(new CustomEvent(`actionclick`, {detail: ev.detail}));
                break;
            }
          },

          editWarningModalSubmit: (ev) => {
            if (ev.detail.action === `confirm`) {
              this.submitSave();
            }
            this.update({editWarningModalOpen: false});
          },

          toggleDescriptionTruncation: () => {
            if (!window.getSelection().toString()) {
              this.update({
                reportDescTruncated: !this.state.reportDescTruncated,
              });
            }
          },
        },
      };
    }

    set bookmarks(bookmarks) {
      this.update({bookmarks});
    }

    get isSaveDisabled() {
      if (this.helpers.hasSelectedBookmark()) {
        return !this.attr(`can-save`) || !this.attr(`has-unsaved-changes`);
      }

      return !this.attr(`can-add-bookmark`);
    }

    get saveButtonLabel() {
      return this.helpers.hasSelectedBookmark() ? `Save Changes` : `Save`;
    }

    public attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);

      if (attr === `selected-bookmark-name`) {
        this.update({reportName: this.attr(`selected-bookmark-name`)});
      }

      if (attr === `selected-bookmark-description`) {
        this.update({reportDesc: this.attr(`selected-bookmark-description`)});
      }

      if (attr === `selected-bookmark-hidden`) {
        this.update({reportHidden: this.attr(`selected-bookmark-hidden`)});
      }

      if (attr === `selected-bookmark-locked`) {
        this.update({reportLocked: this.attr(`selected-bookmark-locked`)});
      }
    }

    /**
     * Checks for presence of a slotted child element
     */
    private hasSlot(slotName: string): boolean {
      return !!this.querySelector(`[slot="${slotName}"]`);
    }

    private changeBookmarkDrawer(open: boolean) {
      const event = `bookmarkdrawer${open ? `open` : `close`}`;

      this.update({bookmarkDrawerOpen: open});
      this.dispatchEvent(new CustomEvent(event));

      if (open) {
        this.track(TrackingEvent.ReportListOpen);
      }
    }

    private launchTour() {
      // @ts-ignore
      const chameleon = window.chmln;

      this.track(TrackingEvent.LaunchTour, {
        'tour id': this.attr(`tour-id`),
        'tour link': this.attr(`tour-link`),
        'with chameleon': !!chameleon,
      });

      if (chameleon && this.attr(`tour-id`)) {
        chameleon.show(this.attr(`tour-id`));
      } else if (this.attr(`tour-link`)) {
        window.open(this.attr(`tour-link`));
      }
    }

    private openSaveModal(saveMode: SaveModes = SaveModes.SaveNew) {
      this.update({
        saveMode,
        saveModalOpen: true,
      });
    }

    private closeSaveModal() {
      this.update({
        saveMode: null,
        saveModalOpen: false,
      });
    }

    private submitSave(newReportData = null) {
      const saveAsNew = this.state.saveMode === SaveModes.SaveNew || !this.helpers.hasSelectedBookmark();

      const reportData = {
        name: this.state.reportName,
        description: this.state.reportDesc,
        hidden: this.state.reportHidden,
        locked: this.state.reportLocked,
        saveAsNew,
        ...(newReportData || {}),
      };

      this.update({
        reportName: reportData.name,
        reportDesc: reportData.description,
        reportHidden: reportData.hidden,
        reportLocked: reportData.locked,
        saveMode: null,
        saveModalOpen: false,
      });

      this.dispatchEvent(new CustomEvent(`save`, {detail: reportData}));

      const currUserId = this.attr(`user-id`);
      const bmUserId = this.attr(`selected-bookmark-user-id`);
      this.track(saveAsNew ? TrackingEvent.ReportSavedAsNew : TrackingEvent.ReportSaved, {
        'is owner': saveAsNew || currUserId === bmUserId,
      });
    }

    private resetReport() {
      this.track(TrackingEvent.ResetState);
      this.dispatchEvent(new CustomEvent(`reset`));
    }

    private getHeaderActionItemsSlots() {
      const controlItems = this.attr(`control-items`);
      return controlItems.filter((item) => item.hasOwnProperty(`slot`));
    }

    private getHeaderActionOverflowItems() {
      const items = this.attr(`overflow-control-items`);
      const isSections = items.length && items[0].hasOwnProperty(`items`);

      // Append Reset to the first section
      if (isSections) {
        return items.map((section, index) => {
          return index === 0 ? {...section, items: this.getDefaultHeaderItems(section.items)} : section;
        });
      }

      return this.getDefaultHeaderItems(items);
    }

    private getDefaultHeaderItems(additionalItems: SectionItem[]): SectionItem[] {
      const hasTourAction = this.attr(`tour-id`) || this.attr(`tour-link`);
      const canWrite = this.attr(`can-write`);
      const hasSelectedBookmark = this.helpers.hasSelectedBookmark();
      const defaultItems = [RESET_ACTION, ...(canWrite && hasSelectedBookmark ? [EDIT_ACTION] : [])];

      return [...defaultItems, ...additionalItems, ...(hasTourAction ? [TOUR_ACTION] : [])];
    }

    private getHeaderActionItems() {
      const controlItems = this.attr(`control-items`);
      const canWrite = this.attr(`can-write`);
      const hasSelectedBookmark = this.helpers.hasSelectedBookmark();

      return [
        // hide save button for limited roles
        ...(canWrite
          ? [
              {
                disabled: this.isSaveDisabled,
                icon: `save`,
                label: this.saveButtonLabel,
                value: HeaderAction.Save,
              },
            ]
          : []),
        // show save-new only when there is a selected bookmark
        ...(canWrite && hasSelectedBookmark
          ? [
              {
                disabled: !this.attr(`can-add-bookmark`),
                icon: `duplicate`,
                label: `Save as New`,
                value: HeaderAction.SaveNew,
              },
            ]
          : []),
        ...(this.attr(`can-load`)
          ? [
              {
                value: HeaderAction.Load,
                label: `Load`,
                icon: `open`,
              },
            ]
          : []),
        ...controlItems,
      ];
    }

    private track(event: string, props: object = {}) {
      if (this.attr(`track-name`)) {
        emitTrackEvent(this, {
          eventName: event,
          eventPrefix: COMPONENT_NAME,
          props: {
            ...props,
            project_id: this.attr(`project-id`),
            tab: this.attr(`track-name`),
            workspace_id: this.attr(`workspace-id`),
          },
          cancelable: true,
        });
      }
    }
  },
);
